import { useState, useEffect } from 'react';
import Footer from '../home/components/Footer';

interface BuilderState {
  product: string;
  size: string;
  quantity: number;
  format: string;
  filling: string;
  extra: string;
}

export default function CustomOrderPage() {
  const [builder, setBuilder] = useState<BuilderState>({
    product: '',
    size: '',
    quantity: 1,
    format: '',
    filling: '',
    extra: ''
  });

  const [totalPrice, setTotalPrice] = useState(0);

  const products = [
    { 
      id: 'empada', 
      name: '🥧 Empada de Frango com Catupiry', 
      basePrice: 10.00,
      image: 'https://public.readdy.ai/ai/img_res/865312417419a789011b2c215cf212ff.jpg'
    },
    { 
      id: 'cappuccino', 
      name: '☕ Cappuccino Raízes', 
      basePrice: 30.00,
      image: 'https://static.readdy.ai/image/e054c862bb8832d032cf0cf58507f2c7/19967eaeecf435ee59bcb0c317db8cf2.jpeg'
    }
  ];

  const sizes = {
    empada: [
      { id: 'individual', name: 'Empada Individual (100g)', price: 10.00 },
      { id: 'empadao', name: 'Empadão (650g)', price: 35.00 }
    ],
    cappuccino: [
      { id: '250g', name: 'Pote 250g', price: 30.00 },
      { id: '1kg', name: 'Embalagem 1kg', price: 100.00 }
    ]
  };

  const formats = [
    { id: 'round', name: 'Redondo Tradicional', price: 0, note: 'nossa forma clássica' },
    { id: 'rectangular', name: 'Retangular (tipo lanche de forno)', price: 5.00, note: 'para empadões 600g/1kg/2kg' }
  ];

  const fillings = [
    { id: 'creamy', name: 'Frango Cremoso com Catupiry', price: 0 },
    { id: 'pepper', name: 'Frango com Pimenta do Reino', price: 2.00 },
    { id: 'noPepper', name: 'Frango Sem Pimenta', price: 0 }
  ];

  const extras = [
    { id: 'none', name: 'Sem Nada', priceSmall: 0, priceLarge: 0 },
    { id: 'catupiry', name: 'Catupiry Original', priceSmall: 2.00, priceLarge: 5.00 },
    { id: 'mozzarella', name: 'Mussarela', priceSmall: 1.50, priceLarge: 4.00 }
  ];

  useEffect(() => {
    calculateTotal();
  }, [builder]);

  const calculateTotal = () => {
    let total = 0;

    if (!builder.product || !builder.size) {
      setTotalPrice(0);
      return;
    }

    // Preço base do tamanho
    const productSizes = sizes[builder.product as keyof typeof sizes];
    const selectedSize = productSizes?.find(s => s.id === builder.size);
    if (selectedSize) {
      total += selectedSize.price;
    }

    // Apenas para empadas
    if (builder.product === 'empada') {
      // Preço do formato
      if (builder.format === 'rectangular' && builder.size !== 'individual') {
        total += 5.00;
      }

      // Preço do recheio
      const selectedFilling = fillings.find(f => f.id === builder.filling);
      if (selectedFilling) {
        total += selectedFilling.price;
      }

      // Preço do extra
      const selectedExtra = extras.find(e => e.id === builder.extra);
      if (selectedExtra) {
        if (builder.size === 'individual') {
          total += selectedExtra.priceSmall;
        } else {
          total += selectedExtra.priceLarge;
        }
      }
    }

    // Multiplicar pela quantidade
    total *= builder.quantity;

    setTotalPrice(total);
  };

  const handleSelect = (step: keyof BuilderState, value: string | number) => {
    if (step === 'product') {
      // Reset tudo quando mudar de produto
      setBuilder({ product: value as string, size: '', quantity: 1, format: '', filling: '', extra: '' });
    } else {
      setBuilder(prev => ({ ...prev, [step]: value }));
    }
  };

  const handleQuantityChange = (delta: number) => {
    setBuilder(prev => ({
      ...prev,
      quantity: Math.max(1, prev.quantity + delta)
    }));
  };

  const isStepComplete = (step: number): boolean => {
    switch(step) {
      case 1: return builder.product !== '';
      case 2: return builder.size !== '';
      case 3: return builder.product !== 'empada' || builder.format !== '';
      case 4: return builder.product !== 'empada' || builder.filling !== '';
      case 5: return builder.product !== 'empada' || builder.extra !== '';
      default: return false;
    }
  };

  const getSummaryText = () => {
    const selectedProduct = products.find(p => p.id === builder.product);
    const productSizes = sizes[builder.product as keyof typeof sizes];
    const selectedSize = productSizes?.find(s => s.id === builder.size);
    
    let summary = `Olá! Gostaria de encomendar:\\n\\n`;
    summary += `${selectedProduct?.name}\\n`;
    summary += `📏 ${selectedSize?.name}\\n`;
    summary += `🔢 Quantidade: ${builder.quantity}\\n`;
    
    if (builder.product === 'empada') {
      const selectedFormat = formats.find(f => f.id === builder.format);
      const selectedFilling = fillings.find(f => f.id === builder.filling);
      const selectedExtra = extras.find(e => e.id === builder.extra);
      
      summary += `📐 ${selectedFormat?.name}\\n`;
      summary += `🍗 ${selectedFilling?.name}\\n`;
      summary += `🧀 ${selectedExtra?.name}\\n`;
    }
    
    summary += `\\n💰 Total: R$ ${totalPrice.toFixed(2)}`;
    return summary;
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5EFE7' }}>
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="cursor-pointer">
            <img 
              src="https://static.readdy.ai/image/e054c862bb8832d032cf0cf58507f2c7/c2b142d97304902a971209e1dcb8c95b.png"
              alt="Retro Flavor DF"
              className="h-16"
            />
          </a>
          <a 
            href="/" 
            className="flex items-center gap-2 text-lg font-semibold hover:opacity-80 transition-opacity cursor-pointer"
            style={{ color: '#1B4965' }}
          >
            <i className="ri-arrow-left-line"></i>
            Voltar
          </a>
        </div>
      </header>

      {/* Main Content */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-4" style={{ color: '#C1272D' }}>
              🎯 Monte Seu Pedido
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Personalize seu pedido do seu jeito! Escolha o produto, tamanho, quantidade e complementos
            </p>
          </div>

          {/* Passo 1: Escolha o Produto */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: isStepComplete(1) ? '#C1272D' : '#1B4965' }}>
                1
              </div>
              <h2 className="text-3xl font-bold" style={{ color: '#1B4965' }}>
                Escolha o Produto
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleSelect('product', product.id)}
                  className={`bg-white rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                    builder.product === product.id ? 'ring-4 shadow-xl' : 'shadow-lg'
                  }`}
                  style={{ 
                    borderColor: builder.product === product.id ? '#C1272D' : 'transparent',
                    ringColor: '#C1272D'
                  }}
                >
                  <div className="w-full h-48 mb-4 rounded-xl overflow-hidden">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="font-bold text-lg text-center" style={{ color: '#1B4965' }}>
                    {product.name}
                  </h3>
                </div>
              ))}
            </div>
          </div>

          {/* Passo 2: Tamanho */}
          {builder.product && (
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: isStepComplete(2) ? '#C1272D' : '#1B4965' }}>
                  2
                </div>
                <h2 className="text-3xl font-bold" style={{ color: '#1B4965' }}>
                  Escolha o Tamanho
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {sizes[builder.product as keyof typeof sizes]?.map((size) => (
                  <div
                    key={size.id}
                    onClick={() => handleSelect('size', size.id)}
                    className={`bg-white rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                      builder.size === size.id ? 'ring-4 shadow-xl' : 'shadow-lg'
                    }`}
                    style={{ 
                      borderColor: builder.size === size.id ? '#C1272D' : 'transparent',
                      ringColor: '#C1272D'
                    }}
                  >
                    <h3 className="font-bold text-lg mb-2" style={{ color: '#1B4965' }}>
                      {size.name}
                    </h3>
                    <p className="text-3xl font-bold" style={{ color: '#C1272D' }}>
                      R$ {size.price.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Passos 3-5: Apenas para Empadas */}
          {builder.product === 'empada' && builder.size && (
            <>
              {/* Passo 3: Formato */}
              <div className="mb-16">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: isStepComplete(3) ? '#C1272D' : '#1B4965' }}>
                    3
                  </div>
                  <h2 className="text-3xl font-bold" style={{ color: '#1B4965' }}>
                    Escolha o Formato
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
                  {formats.map((format) => (
                    <div
                      key={format.id}
                      onClick={() => handleSelect('format', format.id)}
                      className={`bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                        builder.format === format.id ? 'ring-4 shadow-xl' : 'shadow-lg'
                      }`}
                      style={{ 
                        borderColor: builder.format === format.id ? '#C1272D' : 'transparent',
                        ringColor: '#C1272D'
                      }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-xl" style={{ color: '#1B4965' }}>
                          {format.name}
                        </h3>
                        {format.price > 0 && builder.size !== 'individual' && (
                          <span className="text-xl font-bold" style={{ color: '#C1272D' }}>
                            + R$ {format.price.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600">{format.note}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Passo 4: Recheio */}
              {builder.format && (
                <div className="mb-16">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: isStepComplete(4) ? '#C1272D' : '#1B4965' }}>
                      4
                    </div>
                    <h2 className="text-3xl font-bold" style={{ color: '#1B4965' }}>
                      Escolha o Recheio Base
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {fillings.map((filling) => (
                      <div
                        key={filling.id}
                        onClick={() => handleSelect('filling', filling.id)}
                        className={`bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                          builder.filling === filling.id ? 'ring-4 shadow-xl' : 'shadow-lg'
                        }`}
                        style={{ 
                          borderColor: builder.filling === filling.id ? '#C1272D' : 'transparent',
                          ringColor: '#C1272D'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-lg" style={{ color: '#1B4965' }}>
                            {filling.name}
                          </h3>
                          {filling.price > 0 && (
                            <span className="text-xl font-bold" style={{ color: '#C1272D' }}>
                              + R$ {filling.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Passo 5: Complemento */}
              {builder.filling && (
                <div className="mb-16">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl" style={{ backgroundColor: isStepComplete(5) ? '#C1272D' : '#1B4965' }}>
                      5
                    </div>
                    <h2 className="text-3xl font-bold" style={{ color: '#1B4965' }}>
                      Escolha o Complemento
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {extras.map((extra) => (
                      <div
                        key={extra.id}
                        onClick={() => handleSelect('extra', extra.id)}
                        className={`bg-white rounded-2xl p-8 cursor-pointer transition-all duration-300 hover:shadow-xl ${
                          builder.extra === extra.id ? 'ring-4 shadow-xl' : 'shadow-lg'
                        }`}
                        style={{ 
                          borderColor: builder.extra === extra.id ? '#C1272D' : 'transparent',
                          ringColor: '#C1272D'
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="font-bold text-lg" style={{ color: '#1B4965' }}>
                            {extra.name}
                          </h3>
                          {extra.priceSmall > 0 && (
                            <span className="text-xl font-bold" style={{ color: '#C1272D' }}>
                              + R$ {builder.size === 'individual' ? extra.priceSmall.toFixed(2) : extra.priceLarge.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Quantidade e Resumo */}
          {builder.size && (builder.product !== 'empada' || builder.extra) && (
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#1B4965' }}>
                Resumo do Seu Pedido
              </h2>
              
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-lg text-gray-700">Produto:</span>
                  <span className="font-semibold" style={{ color: '#1B4965' }}>
                    {products.find(p => p.id === builder.product)?.name}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-gray-200">
                  <span className="text-lg text-gray-700">Tamanho:</span>
                  <span className="font-semibold" style={{ color: '#1B4965' }}>
                    {sizes[builder.product as keyof typeof sizes]?.find(s => s.id === builder.size)?.name}
                  </span>
                </div>
                
                {builder.product === 'empada' && (
                  <>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-lg text-gray-700">Formato:</span>
                      <span className="font-semibold" style={{ color: '#1B4965' }}>
                        {formats.find(f => f.id === builder.format)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-lg text-gray-700">Recheio:</span>
                      <span className="font-semibold" style={{ color: '#1B4965' }}>
                        {fillings.find(f => f.id === builder.filling)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-gray-200">
                      <span className="text-lg text-gray-700">Complemento:</span>
                      <span className="font-semibold" style={{ color: '#1B4965' }}>
                        {extras.find(e => e.id === builder.extra)?.name}
                      </span>
                    </div>
                  </>
                )}

                {/* Seletor de Quantidade */}
                <div className="flex justify-between items-center py-6 border-b-2 border-gray-300">
                  <span className="text-xl font-bold text-gray-700">Quantidade:</span>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-2xl hover:opacity-80 transition-opacity cursor-pointer"
                      style={{ backgroundColor: '#1B4965' }}
                    >
                      -
                    </button>
                    <span className="text-3xl font-bold w-16 text-center" style={{ color: '#1B4965' }}>
                      {builder.quantity}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(1)}
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-2xl hover:opacity-80 transition-opacity cursor-pointer"
                      style={{ backgroundColor: '#1B4965' }}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#C1272D] to-[#E63946] rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-white">Total:</span>
                  <span className="text-4xl font-bold text-white">
                    R$ {totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>

              <a
                href={`https://wa.me/5561993132519?text=${encodeURIComponent(getSummaryText())}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-4 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: '#25D366' }}
              >
                <i className="ri-whatsapp-fill text-2xl"></i>
                Encomendar pelo WhatsApp
              </a>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
